/**
 * Settings → Daily checks → "Want more?": the starter checklist items that are off at this place (a new place starts lean,
 * core tasks.ts STARTER_ITEMS_ON; owner, 2026-10-11: "It should be fast to startup"). One tap turns one on; one tap turns
 * on the closing routine, or all of them. Items that are on can be turned off again here (undo), so nothing is a trap.
 * The checks the law names are built in (schedule.ts) and never listed here: this list can't hide one.
 */
import { useState } from "react";
import { MORE_CHECKLIST, moreChecklist, type TaskDefinition } from "@logwave/core";
import { Button, Card, ErrorBox, LinkButton, Section, Skeleton, useToast } from "./ui.tsx";
import { useLoad } from "../lib/useLoad.ts";
import { useApi, useSession } from "../session.tsx";

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const cadence = (t: Pick<TaskDefinition, "category" | "schedule">) =>
  t.schedule.perShift ? (t.category === "closing" ? "Every day at closing" : "Every day at opening")
    : t.schedule.everyDays === 7 ? "Once a week" : t.schedule.everyDays === 30 ? "Once a month" : t.schedule.everyDays === 90 ? "Every 3 months"
      : t.schedule.everyDays === 180 ? "Every 6 months" : t.schedule.everyDays === 365 ? "Once a year"
        : t.schedule.everyDays ? `Every ${t.schedule.everyDays} days` : t.schedule.perEvent ? "For each event" : "";

export function WantMoreCard() {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const tasks = useLoad(() => api.tasks(s.org.id), [api, s.org.id]);
  const [busy, setBusy] = useState<string>();
  const [err, setErr] = useState<string>();
  if (!s.can("manager")) return null;
  const here = (tasks.data ?? []).filter((t) => !t.locationId || t.locationId === s.location.id);
  const { off, closingRoutine } = moreChecklist(here, s.location.id);
  const on = here.filter((t) => t.active !== false);

  async function turn(ids: string[], active: boolean, key: string, msg: string) {
    setBusy(key); setErr(undefined);
    try {
      for (const id of ids) await api.updateTask(s.org.id, id, { active });
      toast.show(msg, { icon: "check" });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(undefined); await tasks.reload(); }
  }

  return (
    <Section id="want-more" title={MORE_CHECKLIST.title} kicker="Checklist">
      <Card className="stack">
        <p className="small">{MORE_CHECKLIST.lede}</p>
        {!tasks.data ? <Skeleton rows={3} label="Loading your checklist" /> : (
          <>
            {off.length === 0 && <p className="small" data-testid="want-more-none">Every starter item is on. Turn one off below if it doesn't fit your day.</p>}
            {off.length > 0 && (
              <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
                {closingRoutine.length > 0 && (
                  <Button icon="check" busy={busy === "closing"} disabled={!!busy} onClick={() => void turn(closingRoutine.map((t) => t.id), true, "closing", `Closing routine on: ${plural(closingRoutine.length, "item")} at closing.`)}>
                    {MORE_CHECKLIST.closingRoutine} ({closingRoutine.length})
                  </Button>
                )}
                {off.length > closingRoutine.length && (
                  <Button variant="secondary" busy={busy === "all"} disabled={!!busy} onClick={() => void turn(off.map((t) => t.id), true, "all", `${plural(off.length, "item")} on. They show on Today when they are due.`)}>
                    {MORE_CHECKLIST.all} ({off.length})
                  </Button>
                )}
              </div>
            )}
            {off.length > 0 && (
              <ul className="plain stack-sm" data-testid="want-more-off">
                {off.map((t) => (
                  <li key={t.id} className="row-nowrap" style={{ justifyContent: "space-between", gap: 12 }}>
                    <span><strong>{t.title}</strong><span className="caption"> · {cadence(t)}</span></span>
                    <Button small variant="secondary" busy={busy === t.id} disabled={!!busy} onClick={() => void turn([t.id], true, t.id, `On: ${t.title}.`)}>Turn on</Button>
                  </li>
                ))}
              </ul>
            )}
            {on.length > 0 && (
              <details>
                <summary className="disclose"><span className="small">On now: {plural(on.length, "item")}</span></summary>
                <ul className="plain stack-sm" data-testid="want-more-on">
                  {on.map((t) => (
                    <li key={t.id} className="row-nowrap" style={{ justifyContent: "space-between", gap: 12 }}>
                      <span><strong>{t.title}</strong><span className="caption"> · {cadence(t)}</span></span>
                      <Button small variant="quiet" busy={busy === t.id} disabled={!!busy} onClick={() => void turn([t.id], false, t.id, `Off: ${t.title}.`)}>Turn off</Button>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
        <ErrorBox error={err} />
      </Card>
    </Section>
  );
}

/**
 * Today, under "You're all caught up", for a manager whose day has no checklist items yet: the calm "Want more?" offer at
 * the moment the day's checks are done (the ask comes after the win, never before a safety card). One tap turns on the
 * closing routine; "See all" opens the list in Settings.
 */
export function WantMoreLine({ onDone }: { onDone?: () => void }) {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  async function closing() {
    setBusy(true); setErr(undefined);
    try {
      const { closingRoutine } = moreChecklist((await api.tasks(s.org.id)).filter((t) => !t.locationId || t.locationId === s.location.id), s.location.id);
      for (const t of closingRoutine) await api.updateTask(s.org.id, t.id, { active: true });
      toast.show(closingRoutine.length ? `Closing routine on: ${plural(closingRoutine.length, "item")} at closing.` : "Your closing routine is already on.", { icon: "check" });
      onDone?.();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  return (
    <div className="fcard" data-testid="want-more-line">
      <p className="caption">{MORE_CHECKLIST.title}</p>
      <p className="small">Add the closing routine: surfaces, trash and floors, ticked off at closing.</p>
      <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
        <Button small busy={busy} onClick={() => void closing()}>{MORE_CHECKLIST.closingRoutine}</Button>
        <LinkButton small variant="quiet" to="/settings#want-more">See all</LinkButton>
      </div>
      <ErrorBox error={err} />
    </div>
  );
}
